import { FC, useCallback, useEffect, useState } from "react";
import { Box, Collapse, Grid, Stack, Typography, styled, Modal as MuiModal } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { IconSave } from "@/assets/icons";
import { EMPTY_STR } from "@/constants/string";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  TExamStepsFilterFormExtended,
  EXAM_STEPS_FILTER_FIELD_LABELS,
  EXAM_STEPS_DATE_RANGE_FIELDS,
  formatExamStepsDateValue,
} from "@/features/exam-steps/components/filter/exam-steps-filter-shared";
import { YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { showError } from "@/utils/error";

const TITLE_MAX_LENGTH = 40;

type ExamStepsSaveConditionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string) => void | Promise<void>;
  filterValues: TExamStepsFilterFormExtended;
  // Pre-resolved labels for multi-select / id-valued fields (id → "label1, label2, ...").
  // Free-text / range fields are formatted inline below.
  resolvedLabels?: Partial<Record<keyof TExamStepsFilterFormExtended, string>>;
};

const formatFilterValue = (value: unknown, resolvedLabel?: string): string => {
  if (resolvedLabel) return resolvedLabel;
  if (value === null || value === undefined || value === "") return EMPTY_STR.TEXT;
  if (Array.isArray(value)) return value.length === 0 ? EMPTY_STR.TEXT : value.join("、");
  return String(value);
};

export const ExamStepsSaveConditionModal: FC<ExamStepsSaveConditionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  filterValues,
  resolvedLabels,
}) => {
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [isConditionsExpanded, setIsConditionsExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setTitleError("");
      setIsConditionsExpanded(true);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    onClose();
  }, [isSaving, onClose]);

  const handleSave = useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError("タイトルを入力してください");
      return;
    }
    if (trimmed.length > TITLE_MAX_LENGTH) {
      setTitleError(`${TITLE_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    try {
      setIsSaving(true);
      await onSave(trimmed);
      onClose();
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
  }, [title, onSave, onClose]);

  const nextProcedureEndField = EXAM_STEPS_DATE_RANGE_FIELDS.nextProcedureEndDate;
  const examMonthField = EXAM_STEPS_DATE_RANGE_FIELDS.examMonth;
  const registeredAtField = EXAM_STEPS_DATE_RANGE_FIELDS.registeredAt;

  return (
    <MuiModal open={isOpen} onClose={handleClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <ModalContainer>
        <Typography variant="head20Bold" sx={{ mb: 3 }}>
          適用条件を保存
        </Typography>

        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Typography variant="cap12Light">
            タイトル{" "}
            <Typography component="span" sx={{ color: text.error }}>
              *
            </Typography>
          </Typography>
          <Input
            placeholder="入力してください"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError("");
            }}
            size="md"
            nonControl
            error={Boolean(titleError)}
          />
          <Typography variant="cap12Light" sx={{ color: titleError ? text.error : text.tertiary }}>
            {titleError || `${TITLE_MAX_LENGTH}文字以内で入力してください`}
          </Typography>
        </Stack>

        <ConditionsContainer>
          <ConditionsHeader onClick={() => setIsConditionsExpanded((prev) => !prev)}>
            <Typography variant="sub16Bold">設定した条件</Typography>
            {isConditionsExpanded ? <KeyboardArrowUpIcon sx={{ color: black[80] }} /> : <KeyboardArrowDownIcon sx={{ color: black[80] }} />}
          </ConditionsHeader>

          <Collapse in={isConditionsExpanded}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                {EXAM_STEPS_FILTER_FIELD_LABELS.map(({ key, label }) => (
                  <Grid item xs={4} key={key}>
                    <Stack spacing={0.5}>
                      <Typography variant="cap12Light" sx={{ color: text.secondary }}>
                        {label}
                      </Typography>
                      <Typography variant="body14" sx={{ color: text.primary }}>
                        {formatFilterValue(filterValues[key], resolvedLabels?.[key])}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}

                <Grid item xs={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="cap12Light" sx={{ color: text.secondary }}>
                      {nextProcedureEndField.label}
                    </Typography>
                    <Typography variant="body14" sx={{ color: text.primary }}>
                      {formatExamStepsDateValue(filterValues[nextProcedureEndField.fromKey])} 〜{" "}
                      {formatExamStepsDateValue(filterValues[nextProcedureEndField.toKey])}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="cap12Light" sx={{ color: text.secondary }}>
                      {examMonthField.label}
                    </Typography>
                    <Typography variant="body14" sx={{ color: text.primary }}>
                      {formatExamStepsDateValue(filterValues[examMonthField.fromKey], YEAR_MONTH_FORMAT)} 〜{" "}
                      {formatExamStepsDateValue(filterValues[examMonthField.toKey], YEAR_MONTH_FORMAT)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="cap12Light" sx={{ color: text.secondary }}>
                      {registeredAtField.label}
                    </Typography>
                    <Typography variant="body14" sx={{ color: text.primary }}>
                      {formatExamStepsDateValue(filterValues[registeredAtField.fromKey])} 〜{" "}
                      {formatExamStepsDateValue(filterValues[registeredAtField.toKey])}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </ConditionsContainer>

        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 3 }}>
          <Button variant="ghost" onClick={handleClose} size="sm" disabled={isSaving}>
            キャンセル
          </Button>
          <Button onClick={handleSave} size="sm" startIcon={<IconSave />} isLoading={isSaving}>
            保存
          </Button>
        </Stack>
      </ModalContainer>
    </MuiModal>
  );
};

const ModalContainer = styled(Box)`
  width: 720px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 32px;
  outline: none;

  &:focus {
    outline: none;
  }
`;

const ConditionsContainer = styled(Box)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  overflow: hidden;
`;

const ConditionsHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: ${background.primary};
  }
`;
